import { useRef } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { Dimensions, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { type RouteProp, useRoute } from '@react-navigation/native';
import InfoItem from 'src/presentation/components/molecules/InfoItem';
import Colors from 'src/core/utils/Colors';
import type ApiWorkspaceDetailResponse from 'src/core/models/api/responses/ApiWorkspaceDetailResponse';
import type { ResortScreenParams } from 'src/presentation/navigation/types/NavigationTypes';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import { useResortStore } from 'src/domain/stores/useResortStore';
import { useLeaveAppStore } from 'src/domain/stores/useLeaveAppStore';
import Dimens from 'src/core/utils/Dimens';
import Styles from 'src/core/utils/Styles';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import { SvgIconType } from '@thermenresorts/icons';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';

const { height } = Dimensions.get('window');

const IMAGE_CARD_MIN_HEIGHT = height / 2;

type ResortSection = 'contact' | 'address';

interface ResortHeaderProps {
	workspaceDetail: ApiWorkspaceDetailResponse | null;
	onPressSection: (section: ResortSection) => void;
}

const ResortHeader = ({ workspaceDetail, onPressSection }: ResortHeaderProps) => (
	<View style={styles.imageCard}>
		<ThermenImage
			source={workspaceDetail?.workspace.image ? { uri: workspaceDetail.workspace.image } : null}
			style={styles.headerImage}
		/>
		<Text style={styles.textHeader}>{workspaceDetail?.workspace.title}</Text>
		<View style={styles.headerItems}>
			<Pressable
				accessibilityRole="button"
				style={({ pressed }) => [styles.headerItem, pressed ? styles.headerItemPressed : null]}
				onPress={() => onPressSection('contact')}
			>
				<Text style={styles.headerItemText}>{Translations.resortContact}</Text>
			</Pressable>
			<Pressable
				accessibilityRole="button"
				style={({ pressed }) => [styles.headerItem, pressed ? styles.headerItemPressed : null]}
				onPress={() => onPressSection('address')}
			>
				<Text style={styles.headerItemText}>{Translations.resortRouteDescription}</Text>
			</Pressable>
		</View>
	</View>
);

export default function ResortScreen() {
	const route = useRoute<RouteProp<{ params: ResortScreenParams }>>();
	const workspaceId = route.params.workspaceId;
	const tabBarInset = useTabBarInset();
	const { workspaceDetail, init, getWorkspaceDetail } = useResortStore();
	const { confirmLeaving } = useLeaveAppStore();
	const scrollView = useRef<ScrollView>(null);
	const sectionOffsets = useRef<Record<ResortSection, number>>({ contact: 0, address: 0 });

	const fetchData = () => getWorkspaceDetail({ workspaceId });

	useAppStateScreenListener(
		{
			onInit: () => init(),
			onFocussed: fetchData,
			onForegrounded: fetchData,
		},
		[workspaceId],
	);

	const faqUrl = workspaceDetail?.workspace?.contact?.faq_url ?? null;
	const contactFormUrl = workspaceDetail?.workspace?.contact?.contactform_url ?? null;

	const measureSection = (section: ResortSection) => (event: LayoutChangeEvent) => {
		sectionOffsets.current[section] = event.nativeEvent.layout.y;
	};

	const scrollToSection = (section: ResortSection) =>
		scrollView.current?.scrollTo({ y: sectionOffsets.current[section], animated: true });

	const openInBrowser = (url: string) => confirmLeaving({ url });

	return (
		<View style={styles.mainWrapper}>
			<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }} ref={scrollView}>
				<ResortHeader workspaceDetail={workspaceDetail} onPressSection={scrollToSection} />
				<View style={[styles.wrapper, styles.borderBottom]} onLayout={measureSection('contact')}>
					<View style={Styles.screen.column}>
						<Text style={styles.title}>{workspaceDetail?.contact_details_label}</Text>
						<InfoItem
							label={workspaceDetail?.phone_number_label}
							value={workspaceDetail?.workspace?.contact.phonenumber_international}
						/>
						<InfoItem label={workspaceDetail?.email_label} value={workspaceDetail?.workspace?.contact?.email} />
						<InfoItem
							label={workspaceDetail?.opening_hours_label}
							last={true}
							value={workspaceDetail?.workspace?.openingHours}
						/>

						{faqUrl ? (
							<Pressable style={{ marginTop: Dimens.padding32 }} onPress={() => openInBrowser(faqUrl)}>
								<View accessible={true} style={styles.linkRow}>
									<SvgIcon name={SvgIconType.tooltip} size={Dimens.iconSize16} tintColor={Colors.green} />
									<Text style={styles.linkText}>{workspaceDetail?.faq_label}</Text>
								</View>
							</Pressable>
						) : null}
						{contactFormUrl ? (
							<Pressable style={{ marginTop: Dimens.padding12 }} onPress={() => openInBrowser(contactFormUrl)}>
								<View accessible={true} style={styles.linkRow}>
									<SvgIcon name={SvgIconType.chat} size={Dimens.iconSize16} tintColor={Colors.green} />
									<Text style={styles.linkText}>{workspaceDetail?.contact_form_label}</Text>
								</View>
							</Pressable>
						) : null}
					</View>
				</View>
				<View style={[styles.wrapper]} onLayout={measureSection('address')}>
					<View style={Styles.screen.column}>
						<Text style={styles.title}>{workspaceDetail?.address_details_label}</Text>
						<InfoItem label={workspaceDetail?.street_label} value={workspaceDetail?.workspace?.contact.address} />
						<InfoItem label={workspaceDetail?.postal_code_label} value={workspaceDetail?.workspace?.contact.postalcode} />
						<InfoItem label={workspaceDetail?.city_label} value={workspaceDetail?.workspace?.contact.city} />
						<InfoItem
							label={workspaceDetail?.country_label}
							last={true}
							value={workspaceDetail?.workspace?.contact.country}
						/>

						<Pressable
							style={{ marginTop: Dimens.padding32 }}
							onPress={() => openInBrowser(workspaceDetail?.workspace.contact.navigation_url ?? '')}
						>
							<View accessible={true} style={styles.linkRow}>
								<SvgIcon name={SvgIconType.map} size={Dimens.iconSize16} tintColor={Colors.green} />
								<Text style={styles.linkText}>{workspaceDetail?.plan_route_label}</Text>
							</View>
						</Pressable>
					</View>
				</View>
			</ScrollView>
		</View>
	);
}

const styles = StyleSheet.create({
	mainWrapper: {
		flex: Dimens.flex1,
	},
	imageCard: {
		backgroundColor: Colors.gray400,
		textAlign: 'center',
		justifyContent: 'flex-end',
		minHeight: IMAGE_CARD_MIN_HEIGHT,
	},
	headerImage: {
		width: '100%',
		minHeight: IMAGE_CARD_MIN_HEIGHT,
		position: 'absolute',
	},
	linkRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: Dimens.gap10,
	},
	linkText: {
		fontSize: Dimens.fontSize16,
		fontFamily: Fonts.barlowMedium,
		color: Colors.green,
	},
	textHeader: {
		color: Colors.white,
		fontSize: Dimens.fontSize32,
		fontFamily: Fonts.ptSerif,
		lineHeight: Dimens.lineHeight40,
		paddingHorizontal: Dimens.padding18,
		zIndex: Dimens.zIndexOverlayHigh,
		alignSelf: 'flex-start',
		paddingBottom: Dimens.padding20,
	},
	headerItems: {
		flexDirection: 'row',
		gap: Dimens.gap8,
		paddingHorizontal: Dimens.padding18,
		marginBottom: Dimens.padding25,
		zIndex: Dimens.zIndexOverlayHigh,
	},
	headerItem: {
		width: 'auto',
		backgroundColor: Colors.white4D,
		height: Dimens.size32,
		paddingHorizontal: Dimens.padding16,
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: Dimens.borderRadius4,
	},
	headerItemPressed: {
		backgroundColor: Colors.white66,
	},
	headerItemText: {
		fontFamily: Fonts.barlowMedium,
		color: Colors.white,
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight21,
	},
	wrapper: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingVertical: Dimens.padding32,
		marginHorizontal: Dimens.padding18,
	},
	borderBottom: {
		borderColor: Colors.gray200,
		borderBottomWidth: Dimens.borderWidth1,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize20,
		lineHeight: Dimens.lineHeight30,
		color: Colors.gray750,
		marginBottom: Dimens.padding24,
	},
});
